import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Menu, ChevronDown, X } from "lucide-react";
import logo from "../../assets/logo2.png"; // Adjust path as needed
import { cn } from "@/lib/utils"; // Adjust path as needed
import { Button } from "@/components/ui/button"; // Adjust path as needed
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"; // Adjust path as needed
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
  DrawerHeader,
} from "@/components/ui/drawer"; // Adjust path as needed
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu"; // Adjust path as needed
import { Avatar, AvatarFallback } from "../ui/avatar";

const AdminHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const pathName = location.pathname.split("/")[2]; // Extract base path for admin routes

  const handleLogout = () => {
    localStorage.removeItem("isAdmin"); // Clear admin status
    navigate("/admin-login");
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const adminTabs = [
    { name: "Dashboard", link: "/admin/dashboard" },
    { name: "Company", link: "/admin/company" },
    { name: "Api Hit", link: "/admin/api-hit" },
    { name: "Vehicle", link: "/admin/vehicle" },
    { name: "Order", link: "/admin/order" },
    { name: "Verification", link: "/admin/verification" },
    { name: "Driver", link: "/admin/driver" },
  ];

  // Assuming admin name is stored in localStorage or can be fetched
  const adminName = localStorage.getItem("adminName") || "Admin";

  return (
    <>
      <header className="w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 h-16">
        <div className="flex h-full items-center px-4 md:px-6">
          {/* Left Side: Logo and Mobile Menu Toggle */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden hover:bg-accent rounded-full"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              <Menu className="h-6 w-6 text-muted-foreground" />
            </Button>
            <NavLink to="/admin/vehicle" className="flex items-center gap-2">
              <img
                src={logo}
                alt="Logo"
                className="h-10 w-10 object-contain transition-transform hover:scale-105"
              />
              <span className="text-lg font-medium text-foreground">
                Fastag Tracking Admin
              </span>
            </NavLink>
          </div>

          {/* Right Side: Admin Profile and Logout */}
          <div className="ml-auto flex items-center gap-2">
            <NavigationMenu className="hidden md:flex mx-auto">
              <NavigationMenuList>
                {adminTabs.map((tab) => (
                  <NavigationMenuItem key={tab.name}>
                    <NavLink
                      to={tab.link}
                      className={({ isActive }) =>
                        cn(
                          navigationMenuTriggerStyle(),
                          isActive && "bg-accent text-accent-foreground"
                        )
                      }
                    >
                      {tab.name}
                    </NavLink>
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex items-center cursor-pointer gap-2">
                  <Avatar className="w-9 h-9">
                    <AvatarFallback className="bg-muted text-muted-foreground font-semibold">
                      {adminName?.[0] || "A"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden lg:block font-medium text-foreground">
                    {adminName}
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-64 p-4 bg-background rounded-xl shadow-lg border"
              >
                <div className="text-center font-semibold text-foreground md:hidden mb-4">
                  {adminName}
                </div>
                <Button
                  className="w-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 rounded-lg transition-colors"
                  onClick={handleLogout}
                >
                  Log Out
                </Button>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile Drawer */}
      <Drawer open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DrawerContent side="left" className="w-64 h-full bg-background">
          <DrawerHeader className="flex items-center justify-between p-4 border-b">
            <DrawerTitle>
              <img src={logo} alt="Logo" className="h-12 object-contain" />
            </DrawerTitle>
            <DrawerClose asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive hover:bg-destructive/10 rounded-full"
              >
                <X className="h-6 w-6" />
              </Button>
            </DrawerClose>
          </DrawerHeader>
          <nav className="flex flex-col p-4 gap-2">
            {adminTabs.map((tab) => (
              <Button
                asChild
                key={tab.name}
                variant="ghost"
                className={cn(
                  "text-left justify-start text-foreground font-medium hover:bg-accent hover:text-accent-foreground rounded-lg py-3 px-4 transition-colors",
                  pathName === tab.link.split("/")[2] &&
                    "bg-accent text-accent-foreground"
                )}
              >
                <NavLink to={tab.link} onClick={toggleMenu}>
                  {tab.name}
                </NavLink>
              </Button>
            ))}
          </nav>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default AdminHeader;
